// frontend/src/features/onboarding/steps/DeliveryScheduleStep.tsx
// Redesigned UI — keeps identical handleSubmit data shape and all logic from original.
import React, { useState } from 'react'
import { ArrowRight, ArrowLeft } from 'lucide-react'

interface DeliveryScheduleStepProps {
    eyebrow: string
    accentClass: string
    btnClass: string
    initialData?: {
        morning: { enabled: boolean; time: string }
        afternoon: { enabled: boolean; time: string }
        evening: { enabled: boolean; time: string }
    }
    onNext: (data: any) => void
    onBack: () => void
}

const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
]

function formatTime(t: string) {
    const [h, m] = t.split(':')
    const hour = parseInt(h)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${display}:${m} ${ampm}`
}

const SLOT_OPTIONS = [
    {
        key: 'morning' as const,
        label: 'Morning',
        defaultTime: '08:00',
        suggestedTimes: ['06:00', '07:00', '08:00', '09:00'],
    },
    {
        key: 'afternoon' as const,
        label: 'Afternoon',
        defaultTime: '13:00',
        suggestedTimes: ['12:00', '13:00', '14:00', '15:00'],
    },
    {
        key: 'evening' as const,
        label: 'Evening',
        defaultTime: '18:00',
        suggestedTimes: ['17:00', '18:00', '19:00', '20:00'],
    },
]

type SlotKey = typeof SLOT_OPTIONS[number]['key']

interface SlotState {
    enabled: boolean
    time: string
}

export function DeliveryScheduleStep({
    eyebrow, accentClass, btnClass, initialData, onNext, onBack,
}: DeliveryScheduleStepProps) {
    const [timezone, setTimezone] = useState('America/New_York')
    const [autoSend, setAutoSend] = useState(true)

    const [slots, setSlots] = useState<Record<SlotKey, SlotState>>({
        morning: { enabled: initialData?.morning?.enabled ?? true, time: initialData?.morning?.time ?? '08:00' },
        afternoon: { enabled: initialData?.afternoon?.enabled ?? false, time: initialData?.afternoon?.time ?? '13:00' },
        evening: { enabled: initialData?.evening?.enabled ?? false, time: initialData?.evening?.time ?? '18:00' },
    })

    // Only one slot can be active (radio behaviour matching original setOnly* logic)
    const toggleSlot = (key: SlotKey) => {
        setSlots(prev => {
            const nowEnabled = !prev[key].enabled
            if (nowEnabled) {
                // Disable the others
                const next = { ...prev }
                    ; (Object.keys(next) as SlotKey[]).forEach(k => {
                        next[k] = { ...next[k], enabled: k === key }
                    })
                return next
            }
            return { ...prev, [key]: { ...prev[key], enabled: false } }
        })
    }

    const setTime = (key: SlotKey, time: string) =>
        setSlots(prev => ({ ...prev, [key]: { ...prev[key], time } }))

    const atLeastOne = Object.values(slots).some(s => s.enabled)

    const handleSubmit = () => {
        onNext({
            delivery: {
                morning: { ...slots.morning, timezone },
                afternoon: { ...slots.afternoon, timezone },
                evening: { ...slots.evening, timezone },
            },
            timezone,
            auto_send_enabled: autoSend,
            email_delivery_enabled: true,
        })
    }

    return (
        <>
            <div className="ob-body">
                <div className={`ob-eyebrow ${accentClass}`}>{eyebrow}</div>
                <h2 className="ob-heading">Delivery schedule</h2>
                <p className="ob-sub">
                    Choose when briefings arrive in your inbox. Generate manually anytime from the dashboard.
                </p>

                {/* Slot grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 16 }}>
                    {SLOT_OPTIONS.map(opt => {
                        const slot = slots[opt.key]
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => toggleSlot(opt.key)}
                                style={{
                                    border: `0.5px solid ${slot.enabled ? 'rgba(0,255,192,0.4)' : 'var(--color-border-tertiary)'}`,
                                    borderRadius: 9, padding: '10px 6px', textAlign: 'center', cursor: 'pointer',
                                    background: slot.enabled ? 'rgba(0,255,192,0.04)' : 'var(--color-background-secondary)',
                                    transition: 'all 0.12s',
                                }}
                            >
                                <div style={{
                                    fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
                                    color: slot.enabled ? 'rgba(0,255,192,0.7)' : 'var(--color-text-secondary)',
                                }}>{opt.label}</div>
                                <div style={{
                                    fontSize: 15, fontWeight: 500,
                                    color: slot.enabled ? '#00FFC0' : 'var(--color-text-secondary)',
                                }}>{formatTime(slot.time)}</div>
                                {slot.enabled
                                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00FFC0" strokeWidth="2.5" style={{ marginTop: 4, display: 'block', margin: '4px auto 0' }} aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                                    : <div style={{ height: 16 }} />
                                }
                            </button>
                        )
                    })}
                </div>

                {/* Time picker — show for active slot */}
                {SLOT_OPTIONS.filter(o => slots[o.key].enabled).map(opt => (
                    <div key={opt.key} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            {opt.label} time
                        </div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {opt.suggestedTimes.map(t => (
                                <button
                                    key={t} type="button"
                                    onClick={() => setTime(opt.key, t)}
                                    style={{
                                        padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                                        border: `0.5px solid ${slots[opt.key].time === t ? 'rgba(0,255,192,0.4)' : 'var(--color-border-tertiary)'}`,
                                        background: slots[opt.key].time === t ? 'rgba(0,255,192,0.06)' : 'var(--color-background-secondary)',
                                        color: slots[opt.key].time === t ? '#00FFC0' : 'var(--color-text-secondary)',
                                    }}
                                >{formatTime(t)}</button>
                            ))}
                            <input
                                type="time" value={slots[opt.key].time}
                                onChange={e => setTime(opt.key, e.target.value)}
                                style={{
                                    padding: '5px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                                    border: '0.5px solid var(--color-border-tertiary)',
                                    background: 'var(--color-background-secondary)',
                                    color: 'var(--color-text-primary)',
                                }}
                            />
                        </div>
                    </div>
                ))}

                {/* Timezone */}
                <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginBottom: 5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Timezone
                    </div>
                    <select
                        value={timezone}
                        onChange={e => setTimezone(e.target.value)}
                        style={{
                            width: '100%', padding: '8px 11px', borderRadius: 7, fontSize: 12,
                            border: '0.5px solid rgba(102,0,255,0.3)',
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-primary)',
                            cursor: 'pointer',
                        }}
                    >
                        {TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                    </select>
                </div>

                {/* Auto-send toggle */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: 8,
                    border: `0.5px solid ${autoSend ? 'rgba(102,0,255,0.25)' : 'var(--color-border-tertiary)'}`,
                    background: autoSend ? 'rgba(102,0,255,0.05)' : 'var(--color-background-secondary)',
                    marginBottom: 2,
                }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>Auto-send enabled</div>
                        <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 1 }}>
                            Briefings are automatically emailed at your scheduled times
                        </div>
                    </div>
                    <button
                        type="button"
                        className={`ob-tog${autoSend ? '' : ' ob-tog--off'}`}
                        onClick={() => setAutoSend(v => !v)}
                        aria-label="Toggle auto-send"
                    />
                </div>

                <p style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 8, opacity: 0.6 }}>
                    Adjustable anytime in Settings → Delivery.
                </p>
            </div>

            {/* Footer */}
            <div className="ob-footer">
                <button type="button" className="ob-btn-back" onClick={onBack}>
                    <ArrowLeft size={13} /> Back
                </button>
                <button
                    type="button"
                    className={`ob-btn-next ${btnClass}`}
                    onClick={handleSubmit}
                    disabled={!atLeastOne}
                >
                    Finish setup <ArrowRight size={13} />
                </button>
            </div>
        </>
    )
}